'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { settingsApi } from '@/lib/api';

export function WhatsAppFloat() {
  const t = useTranslations('whatsappFloat');
  const [phone, setPhone] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const pathname = usePathname();

  // Only the DETAIL pages (/courses/slug, /tests/slug, /tests/bundle/slug,
  // /events/slug) have a fixed bottom CTA bar on mobile (lg:hidden, ~66px tall).
  // Index/coming-soon pages (/courses, /tests, /events) have no bar, so require
  // a slug — otherwise the button floats too high on those pages.
  const hasBottomBar = /^\/(courses|tests|events)\/[^/]/.test(pathname);

  // Kontak page is already a contact page — WAB is redundant there.
  const suppress = pathname === '/kontak';

  useEffect(() => {
    if (suppress) return;
    // Delayed entrance — don't distract on immediate page load
    const timer = setTimeout(() => setVisible(true), 2000);

    settingsApi.getPublic().then((s) => {
      const raw = String(s['contact.whatsapp'] ?? s['contact.phone'] ?? '');
      if (raw) {
        // Normalise to international format without + or spaces
        const normalised = raw.replace(/[^0-9]/g, '').replace(/^0/, '62');
        setPhone(normalised);
      }
    }).catch(() => {});

    return () => clearTimeout(timer);
  }, [suppress]);

  if (suppress) return null;
  if (!phone) return null;

  const message = encodeURIComponent(t('prefillMessage'));
  const href = `https://wa.me/${phone}?text=${message}`;

  // bottom-20 (5rem) clears the sticky CTA bar's ~96px baseline height; add
  // the safe-area inset on top so it keeps clearing the bar on notched
  // devices, where the bar itself grows by that same inset (see the detail
  // clients' pb-[calc(0.75rem+env(safe-area-inset-bottom))] bars).
  const bottomClass = hasBottomBar
    ? 'bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6'
    : 'bottom-6';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('chatLabel')}
      className={`fixed ${bottomClass} right-6 z-50 flex items-center gap-2.5 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {/* Tooltip label */}
      <span className="hidden sm:block bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        {t('chatLabel')}
      </span>

      {/* Button */}
      <div className="w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
           style={{ backgroundColor: '#25D366' }}>
        {/* WhatsApp SVG icon */}
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </div>
    </a>
  );
}
